import { Network, NETWORKS } from 'src/common/types/network';
import { isValidStellarContractAddress, STELLAR_NATIVE_XLM_CONTRACT } from 'src/common/utils/non-evm-currency';
import { LiquidityPoolDetailsDTO } from 'src/modules/liquidity-pools/infra';
import * as Yup from 'yup';

import { EthereumAddress } from '@/utils/currency';
import { NON_DECIMAL_NUMBERS_REGEX, ONLY_DECIMAL_POSITIVE_NUMBERS_REGEX, WALLET_REGEX } from '@/utils/regex';

import {
  AmbientVolumeType,
  GearboxMode,
  LiquidLocker,
  OrderlyRevenueType,
  OrderlyVolumeType,
  TriggerContext,
  TriggerType,
  UserIdentifierProperty,
} from '../types';
import {
  INITIAL_UTILIZATION_THRESHOLD_FORM_VALUES,
  ParsedUtilizationCondition,
  UtilizationThresholdFormValues,
} from './blendUtilization';

export * from './blendUtilization';

export type TriggerConditionComparator = '==' | '!=' | '>' | '<' | '<=' | '>=' | 'contains' | 'notContains';
export type TriggerConditionComparatorToLabelMap = {
  [key in TriggerConditionComparator]: string;
};

export interface TriggerCondition {
  name: string;
  comparator: TriggerConditionComparator;
  value: string;
}

export interface ContractFragmentInput {
  name: string;
  type: string;
  internalType: string;
}

export interface ContractFragment {
  signature: string;
  type: string;
  inputs: ContractFragmentInput[];
  stateMutability: string;
}

export interface Contract {
  id: string;
  network: string;
  chainId: number;
  address: string;
  functions: ContractFragment[];
  events: ContractFragment[];
}

// Page-keyed UI-settings shapes for triggers. Mirror conversion-side
// `ConversionDisplayName` etc. but skip the entity-id field — the Record key
// in editor state already carries the trigger id.
export interface TriggerDisplayName {
  endUserOnboardingPage?: string;
  incentivesTemplatePage?: string;
}

export interface TriggerDescription {
  endUserOnboardingPage?: string;
  incentivesTemplatePage?: string;
}

export interface TriggerVisibility {
  endUserOnboardingPage?: boolean;
  incentivesTemplatePage?: boolean;
}

export interface TriggerSortOrder {
  endUserOnboardingPage?: number;
  incentivesTemplatePage?: number;
}

export interface TriggerCtaConfigItem {
  url: string;
  text: string;
  enabled?: boolean;
}

export interface TriggerCtaConfig {
  endUserOnboardingPage?: TriggerCtaConfigItem;
  incentivesTemplatePage?: TriggerCtaConfigItem;
}

export interface TriggerTiersModalVisibility {
  endUserOnboardingPage?: boolean;
  incentivesTemplatePage?: boolean;
}

export interface TriggerEnabledAsReferralCard {
  endUserOnboardingPage?: boolean;
  incentivesTemplatePage?: boolean;
}

export type TriggerPageKey = 'endUserOnboardingPage' | 'incentivesTemplatePage';

export interface TriggerEventFormFieldDomain {
  label: string;
  type: string;
  options?: string[];
  required?: boolean;
  placeholder?: string;
}

export interface TriggerEventFormStructureDomain {
  id: string;
  visible: boolean;
  name: string;
  description: string;
  fields: Record<string, TriggerEventFormFieldDomain>;
  signature: string;
  submitButtonText: string;
}

export type TriggerEventFormsConfigDomain = Partial<Record<TriggerPageKey, TriggerEventFormStructureDomain | null>>;

export interface TriggerDomainUiSettings {
  displayName?: TriggerDisplayName;
  description?: TriggerDescription;
  visibility?: TriggerVisibility;
  sortOrder?: TriggerSortOrder;
  ctaConfig?: TriggerCtaConfig;
  tiersModalVisibility?: TriggerTiersModalVisibility;
  enabledAsReferralCard?: TriggerEnabledAsReferralCard;
  triggerEventForms?: TriggerEventFormsConfigDomain;
}

export interface Trigger {
  description: string;
  id: string;
  name: string;
  type: EventType;
  ref: string;
  contractAddress?: string;
  signature: string;
  conditionExpression: string | null;
  contracts?: Contract[];
  volumeExpression: string | null;
  revenueExpression: string | null;
  currencyExpression: string | null;
  currencyVolumeExpression: string | null;
  currencyRevenueExpression: string | null;
  endUserIdentifierExpression: string | null;
  endUserIdentifierProperty: UserIdentifierProperty | null;
  triggerType: TriggerType;
  context: TriggerContext | null;
  uiSettings?: TriggerDomainUiSettings;
}

export type OnChainTriggerAdvancedFilterType = 'address' | 'uint' | 'string' | 'bytes' | 'bool';
export type OffChainTriggerAdvancedFilterType = 'string' | 'int' | 'boolean';

export enum UintArgumentUnitType {
  Wei = 'wei',
  Ether = 'ether',
}

export interface OnChainTriggerAdvancedFilter {
  argument: string;
  operator: string;
  value: string | number;
  type: OnChainTriggerAdvancedFilterType;
  unit?: UintArgumentUnitType;
}

export interface OffChainTriggerAdvancedFilter {
  argument: string;
  operator: string;
  value: string | number;
  type: OffChainTriggerAdvancedFilterType;
  unit?: UintArgumentUnitType;
}

export type InsertSmartContractsFormValues = {
  contract: {
    chainId: number;
    contractAddress: string;
  };
  sameAbi: boolean;
  isTestnet: boolean;
};

export enum FilterMeetCondition {
  ALL = 'All',
  ANY = 'Any',
}

export enum EventType {
  OnChainFunction = 'on-chain-function',
  OnChainInternalFunction = 'on-chain-internal-function',
  OnChainEvent = 'on-chain-event',
  OffChainEvent = 'off-chain-event',
  OnChainSubgraphEvent = 'on-chain-subgraph',
  OnChainTokenHolder = 'on-chain-token-holder',
  onChainUniSwapV3LiquidityPoolEvent = 'on-chain-uniswap-liquidity-pool',
  TrackingEvent = 'tracking-event',
}

export const EventTypeHumanized: Record<EventType, string> = {
  [EventType.OnChainFunction]: 'On-Chain Function',
  [EventType.OnChainInternalFunction]: 'On-Chain Internal Function',
  [EventType.OnChainEvent]: 'On-Chain Event',
  [EventType.OffChainEvent]: 'Off-Chain Event',
  [EventType.OnChainSubgraphEvent]: 'On-Chain Subgraph Event',
  [EventType.OnChainTokenHolder]: 'On-Chain Token Holder',
  [EventType.onChainUniSwapV3LiquidityPoolEvent]: 'On-Chain Uniswap V3 Liquidity Pool Event',
  [EventType.TrackingEvent]: 'Tracking Event',
};

const eventTypeTranslations: Record<EventType, string> = {
  [EventType.TrackingEvent]: 'Tracking event',
  [EventType.OffChainEvent]: 'Off-chain event',
  [EventType.OnChainEvent]: 'On-chain event',
  [EventType.OnChainFunction]: 'On-chain function',
  [EventType.OnChainInternalFunction]: 'On-chain internal function',
  [EventType.OnChainTokenHolder]: 'Token balance event',
  [EventType.OnChainSubgraphEvent]: 'Subgraph event',
  [EventType.onChainUniSwapV3LiquidityPoolEvent]: 'UniSwap V3 Liquidity Pool event',
};

export const translateEventType = (type: EventType): string => {
  return eventTypeTranslations[type] || type;
};

export enum EventTriggerType {
  OnChain = 'OnChain',
  OnChainFunction = 'OnChainFunction',
  Github = 'Github',
  Discord = 'Discord',
  CustomEvent = 'CustomEvent',
  OnChainTokenHolder = 'OnChainTokenHolder',
  OnChainV2Liquidity = 'OnchainV2Liquidity',
  XFollowEvent = 'XFollowEvent',
  XPostEvent = 'XPostEvent',
  XRepostEvent = 'XRepostEvent',
  XLikeEvent = 'XLikeEvent',
  OnChainSubgraphEvent = 'OnChainSubgraphEvent',
  Uniswap = 'Uniswap',
  UniswapV4 = 'UniswapV4',
  LynxTrade = 'LynxTrade',
  NadoTrade = 'NadoTrade',
  NadoPurpsTrade = 'NadoPurpsTrade',
  Dune = 'Dune',
  Morpho = 'Morpho',
  Pendle = 'Pendle',
  PendleLP = 'PendleLP',
  Zealy = 'Zealy',
  Galxe = 'Galxe',
  BalancerPool = 'BalancerPool',
  CompoundV3 = 'compound-v3',
  MorphoBorrowerStrategy = 'morpho-borrower-strategy',
  HypurrNetPoolPositions = 'hypurr-net-pool-positions',
  HypurrEulerNetPositions = 'hypurr-euler-net-positions',
  HyperswapLiquidityV3 = 'HyperSwap',
  HybraLiquidityV3 = 'Hybra',
  PrjXLiquidityV3 = 'Prj X',
  KittenSwapLiquidityV3 = 'Kittenswap',
  UltrasolidLiquidityV3 = 'Ultrasolid',
  UpheavalLiquidityV3 = 'Upheaval',
  HibachiTrading = 'hibachi-trading',
  HibachiAffiliates = 'hibachi-affiliates',
  OrderlyBuilderTrades = 'orderly-builder-trades',
  SuiTokenHolders = 'SuiTokenHolders',
  SolanaTokenHolders = 'SolanaTokenHolders',
  HyperliquidTrade = 'HyperliquidTrade',
  AmbientTrade = 'ambient-trade',
  ValiantTrade = 'ValiantTrade',
  ValiantLiquidity = 'ValiantLiquidity',
  Pyron = 'Pyron',
  FogoLend = 'FogoLend',
  FelixVanillaCollateral = 'FelixVanillaCollateral',
  FelixCdpCollateral = 'FelixCdpCollateral',
  FogoTokenHolders = 'FogoTokenHolders',
  StellarTokenHolders = 'StellarTokenHolders',
  BlendPositions = 'BlendPositions',
  AquariusLiquidity = 'AquariusLiquidity',
  AquariusSwaps = 'AquariusSwaps',
  HypurrCardTransactions = 'HypurrCardTransactions',
  NestLiquidityV3 = 'Nest',
  FogoIgnitionLocked = 'FogoIgnitionLocked',
  PolymarketTradeVolume = 'PolymarketTradeVolume',
  Gearbox = 'Gearbox',
  PacificaBuilderTrades = 'PacificaBuilderTrades',
  OdysseyBuilderTrades = 'OdysseyBuilderTrades',
  MorphoVaults = 'MorphoVaults',
  EulerV2Looping = 'EulerV2Looping',
  EulerVaults = 'EulerVaults',
  SpectraYT = 'SpectraYT',
  SpectraLP = 'SpectraLP',
  DiscordServerJoin = 'DiscordServerJoin',
  DiscordRoleAssigned = 'DiscordRoleAssigned',
  StripePayment = 'stripe-payment',
  Exponent = 'Exponent',
}

export enum XTriggerAction {
  Follow = 'follow',
  Post = 'post',
}

export enum MoneyMarketPositionType {
  Lending = 'lending',
  Borrowing = 'borrowing',
}

export enum PyronStrategy {
  Lend = 'Lend',
  Borrow = 'Borrow',
}

export enum FogoLendStrategy {
  Deposit = 'Deposit',
  Borrow = 'Borrow',
}

export enum HypurrNetPoolPositionsStrategy {
  Borrow = 'Borrow',
  Deposit = 'Deposit',
}

export enum HypurrEulerNetPositionsStrategy {
  Borrow = 'Borrow',
  Deposit = 'Deposit',
}

export enum HibachiAffiliateReferredFeesLevel {
  Level1 = 'level1',
  Level2 = 'level2',
  Level3 = 'level3',
}

export interface EditTriggerFormValues extends Partial<UtilizationThresholdFormValues> {
  id: string;
  name: string;
  description: string;
  volume_currency_expression?: string;
  endUserIdentifierProperty?: UserIdentifierProperty;
  originalUtilizationCondition?: ParsedUtilizationCondition;
}

export interface OnChainTriggerFormValues {
  description: string;
  filterMeetCondition: FilterMeetCondition;
  filters?: OnChainTriggerAdvancedFilter[];
  hasAdvancedFilters?: boolean;
  name: string;
  signature: string;
  type: EventType;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface OffChainTriggerFormValues {
  name: string;
  description: string;
  signature: string;
  hasAdvancedFilters?: boolean;
  filterMeetCondition: FilterMeetCondition;
  filters?: OffChainTriggerAdvancedFilter[];
  endUserIdentifierProperty: UserIdentifierProperty;
  endUserIdentifierExpression?: string;
}

export interface TokenHoldersTriggerFormValues {
  name: string;
  chainId: number;
  tokenAddress: string;
  tokenCurrency: string;
  useTokenCurrencyReference: boolean;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface SuiTokenHoldersTriggerFormValues {
  tokenAddress: string;
  tokenCurrency: string;
  useTokenCurrencyReference: boolean;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface SolanaTokenHoldersTriggerFormValues {
  name: string;
  tokenAddress: string;
  tokenCurrency: string;
  useTokenCurrencyReference: boolean;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface FogoTokenHoldersTriggerFormValues {
  name: string;
  tokenAddress: string;
  tokenCurrency: string;
  useTokenCurrencyReference: boolean;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface StellarTokenHoldersTriggerFormValues {
  name: string;
  tokenAddress: string;
  tokenCurrency: string;
  useTokenCurrencyReference: boolean;
  endUserIdentifierProperty: UserIdentifierProperty;
}

/**
 * Which side of a Blend reserve the trigger rewards. The values are the strings the server preset's
 * schema accepts, so they travel to the DTO unchanged.
 */
export enum BlendPositionSide {
  Supply = 'supply',
  Borrow = 'borrow',
}

export interface BlendTriggerFormValues extends UtilizationThresholdFormValues {
  poolAddress: string;
  asset: string;
  type: BlendPositionSide;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface BalancerTriggerFormValues {
  name: string;
  chainId: number;
  tokenAddress: string; // This will be the Balancer Pool Address
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface HibachiAffiliatesTriggerFormValues {
  referredFeesLevel: HibachiAffiliateReferredFeesLevel;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface AmbientTradeTriggerFormValues {
  volumeType: AmbientVolumeType;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface MoneyMarketTriggerFormValues {
  chainId: number;
  marketAddress: string;
  positionType: MoneyMarketPositionType;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface PyronTriggerFormValues {
  asset: string;
  market: string;
  strategy: PyronStrategy;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface SubgraphBalanceTriggerFormValues {
  chainId: number;
  subgraphName: string;
  tokenCurrency?: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_SUBGRAPH_BALANCE_TRIGGER_FORM_VALUES: SubgraphBalanceTriggerFormValues = {
  chainId: 1,
  subgraphName: '',
  tokenCurrency: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export interface DuneTriggerFormValues {
  name: string;
  description: string;
  queryUrl: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_DUNE_TRIGGER_FORM_VALUES: DuneTriggerFormValues = {
  name: '',
  description: '',
  queryUrl: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const duneTriggerFormSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),

  description: Yup.string().optional(),

  queryUrl: Yup.string()
    .required('Dune query URL is required')
    .matches(
      /^https:\/\/dune\.com\/queries\/\d+$/,
      'Must be a valid Dune query URL with an ID, e.g., https://dune.com/queries/123456',
    ),

  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const INITIAL_TOKEN_HOLDERS_TRIGGER_FORM_VALUES: TokenHoldersTriggerFormValues = {
  name: '',
  chainId: 1,
  tokenAddress: '',
  tokenCurrency: '',
  useTokenCurrencyReference: false,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_SUI_TOKEN_HOLDERS_TRIGGER_FORM_VALUES: SuiTokenHoldersTriggerFormValues = {
  tokenAddress: '',
  tokenCurrency: '',
  useTokenCurrencyReference: false,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_SOLANA_TOKEN_HOLDERS_TRIGGER_FORM_VALUES: SolanaTokenHoldersTriggerFormValues = {
  name: '',
  tokenAddress: '',
  tokenCurrency: '',
  useTokenCurrencyReference: false,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_FOGO_TOKEN_HOLDERS_TRIGGER_FORM_VALUES: FogoTokenHoldersTriggerFormValues = {
  name: '',
  tokenAddress: '',
  tokenCurrency: '',
  useTokenCurrencyReference: false,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_STELLAR_TOKEN_HOLDERS_TRIGGER_FORM_VALUES: StellarTokenHoldersTriggerFormValues = {
  name: '',
  tokenAddress: '',
  tokenCurrency: '',
  useTokenCurrencyReference: false,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

/**
 * One pool address and nothing else. Aquarius has three pool types and the connector handles all
 * three, so the user never picks one; and a liquidity position has no side, unlike Blend.
 */
export interface AquariusTriggerFormValues {
  poolAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_AQUARIUS_TRIGGER_FORM_VALUES: AquariusTriggerFormValues = {
  poolAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

/**
 * Same single input as the liquidity trigger: every swap through the pool counts, in whichever of
 * its two tokens was sold, so there is no token or direction to pick.
 */
export interface AquariusSwapsTriggerFormValues {
  poolAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_AQUARIUS_SWAPS_TRIGGER_FORM_VALUES: AquariusSwapsTriggerFormValues = {
  poolAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_BLEND_TRIGGER_FORM_VALUES: BlendTriggerFormValues = {
  poolAddress: '',
  asset: '',
  type: BlendPositionSide.Supply,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
  ...INITIAL_UTILIZATION_THRESHOLD_FORM_VALUES,
};

export const INITIAL_BALANCER_TRIGGER_FORM_VALUES: BalancerTriggerFormValues = {
  name: '',
  chainId: 1,
  tokenAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_HIBACHI_AFFILIATES_TRIGGER_FORM_VALUES: HibachiAffiliatesTriggerFormValues = {
  referredFeesLevel: HibachiAffiliateReferredFeesLevel.Level1,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_AMBIENT_TRADE_TRIGGER_FORM_VALUES: AmbientTradeTriggerFormValues = {
  volumeType: AmbientVolumeType.AllVolume,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_COMPOUND_V3_TRIGGER_FORM_VALUES: MoneyMarketTriggerFormValues = {
  chainId: 1,
  marketAddress: '',
  positionType: MoneyMarketPositionType.Lending,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_PYRON_TRIGGER_FORM_VALUES: PyronTriggerFormValues = {
  asset: '',
  market: '',
  strategy: PyronStrategy.Lend,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export interface FogoLendTriggerFormValues {
  strategy: FogoLendStrategy;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_FOGOLEND_TRIGGER_FORM_VALUES: FogoLendTriggerFormValues = {
  strategy: FogoLendStrategy.Deposit,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const tokenHoldersTriggerValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is a required field'),
  chainId: Yup.number().required('Network is a required field'),
  tokenAddress: Yup.string().required('Token address is a required field'),
  tokenCurrency: Yup.string().when('useTokenCurrencyReference', {
    is: true,
    then: (schema) => schema.required('Currency is a required field'),
    otherwise: (schema) => schema.optional(),
  }),
  useTokenCurrencyReference: Yup.boolean(),
});

export const suiTokenHoldersTriggerValidationSchema = Yup.object().shape({
  tokenAddress: Yup.string().required('Token address is a required field'),
  tokenCurrency: Yup.string().when('useTokenCurrencyReference', {
    is: true,
    then: (schema) => schema.required('Currency is a required field'),
    otherwise: (schema) => schema.optional(),
  }),
  useTokenCurrencyReference: Yup.boolean(),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const solanaTokenHoldersTriggerValidationSchema = Yup.object().shape({
  name: Yup.string().optional(),
  tokenAddress: Yup.string().required('Token address is a required field'),
  tokenCurrency: Yup.string().when('useTokenCurrencyReference', {
    is: true,
    then: (schema) => schema.required('Currency is a required field'),
    otherwise: (schema) => schema.optional(),
  }),
  useTokenCurrencyReference: Yup.boolean(),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const fogoTokenHoldersTriggerValidationSchema = Yup.object().shape({
  name: Yup.string().optional(),
  tokenAddress: Yup.string().required('Token address is a required field'),
  tokenCurrency: Yup.string().when('useTokenCurrencyReference', {
    is: true,
    then: (schema) => schema.required('Currency is a required field'),
    otherwise: (schema) => schema.optional(),
  }),
  useTokenCurrencyReference: Yup.boolean(),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const stellarTokenHoldersTriggerValidationSchema = Yup.object().shape({
  name: Yup.string().optional(),
  tokenAddress: Yup.string()
    .required('Token address is a required field')
    // Stellar StrKeys carry a CRC16 checksum, so a mistyped address is caught here rather than
    // producing a trigger signature that silently matches no event.
    .test(
      'is-stellar-contract',
      'Enter a valid Stellar contract address (starts with C, 56 uppercase characters)',
      (value) => !value || isValidStellarContractAddress(value),
    )
    .test(
      'is-not-native-xlm',
      'Native XLM cannot be tracked. Every Stellar account holds XLM without a trustline, so there is no holder list to read.',
      (value) => !value || value !== STELLAR_NATIVE_XLM_CONTRACT,
    ),
  tokenCurrency: Yup.string().when('useTokenCurrencyReference', {
    is: true,
    then: (schema) => schema.required('Currency is a required field'),
    otherwise: (schema) => schema.optional(),
  }),
  useTokenCurrencyReference: Yup.boolean(),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

/**
 * Pool and asset are both Stellar contracts (`C…`) and are validated the same way. They are
 * separate fields rather than one address because the pair is what identifies a reserve: a pool
 * holds several, and each carries its own rate and utilization.
 */
export const aquariusTriggerValidationSchema = Yup.object().shape({
  poolAddress: Yup.string()
    .required('Pool address is a required field')
    // Stellar StrKeys carry a CRC16 checksum, so a mistyped address is caught here rather than
    // producing a trigger signature that silently matches no event.
    .test(
      'is-stellar-contract',
      'Enter a valid Stellar contract address (starts with C, 56 uppercase characters)',
      (value) => !value || isValidStellarContractAddress(value),
    ),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const aquariusSwapsTriggerValidationSchema = Yup.object().shape({
  poolAddress: Yup.string()
    .required('Pool address is a required field')
    .test(
      'is-stellar-contract',
      'Enter a valid Stellar contract address (starts with C, 56 uppercase characters)',
      (value) => !value || isValidStellarContractAddress(value),
    ),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

const utilizationThresholdValidationFields = {
  utilizationThresholdEnabled: Yup.boolean(),
  utilizationOperator: Yup.string()
    .nullable()
    .when('utilizationThresholdEnabled', {
      is: true,
      then: (schema) => schema.required('Operator is required'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  utilizationPercentage: Yup.number()
    // Formik hands an emptied number input over as '', which would otherwise fail as "not a number".
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .nullable()
    .when('utilizationThresholdEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Utilization is required')
          .min(0, 'Utilization must be between 0 and 100')
          .max(100, 'Utilization must be between 0 and 100'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
};

export const blendTriggerValidationSchema = Yup.object().shape({
  poolAddress: Yup.string()
    .required('Pool address is a required field')
    .test(
      'is-stellar-contract',
      'Enter a valid Stellar contract address (starts with C, 56 uppercase characters)',
      (value) => !value || isValidStellarContractAddress(value),
    ),
  // Native XLM is accepted here, unlike the token-holders trigger that rejects it. Blend keeps
  // positions in the pool contract's storage rather than in trustlines, so an XLM reserve has a
  // readable position list — and XLM is a reserve in every live pool.
  asset: Yup.string()
    .required('Asset address is a required field')
    .test(
      'is-stellar-contract',
      'Enter a valid Stellar contract address (starts with C, 56 uppercase characters)',
      (value) => !value || isValidStellarContractAddress(value),
    )
    .test(
      'is-not-pool',
      'The asset is one of the pool\u2019s reserves, not the pool itself.',
      (value, ctx) => !value || value !== ctx.parent.poolAddress,
    ),
  type: Yup.mixed<BlendPositionSide>().required('Side is a required field'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
  ...utilizationThresholdValidationFields,
});

export const balancerTriggerValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  chainId: Yup.number().required('Network is required'),
  tokenAddress: Yup.string()
    .required('Balancer Pool address is required')
    .matches(WALLET_REGEX, 'Invalid address format'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const compoundV3TriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is required'),
  marketAddress: Yup.string().required('Market address is required').matches(WALLET_REGEX, 'Invalid address format'),
  positionType: Yup.mixed<MoneyMarketPositionType>().required('Position type is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const pyronTriggerValidationSchema = Yup.object().shape({
  asset: Yup.string().required('Asset address is required'),
  market: Yup.string().required('Market address is required'),
  strategy: Yup.mixed<PyronStrategy>().required('Strategy is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const fogoLendTriggerValidationSchema = Yup.object().shape({
  strategy: Yup.mixed<FogoLendStrategy>().required('Strategy is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface FogoIgnitionLockedTriggerFormValues {
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_FOGO_IGNITION_LOCKED_TRIGGER_FORM_VALUES: FogoIgnitionLockedTriggerFormValues = {
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const fogoIgnitionLockedTriggerValidationSchema = Yup.object().shape({
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface PolymarketTradeVolumeTriggerFormValues {
  conditionId: string;
  name: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_POLYMARKET_TRADE_VOLUME_TRIGGER_FORM_VALUES: PolymarketTradeVolumeTriggerFormValues = {
  conditionId: '',
  name: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const polymarketTradeVolumeEditValidationSchema = Yup.object().shape({
  conditionId: Yup.string().required('Market ID is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface PolymarketTradeVolumeCreateFormValues {
  eventSlugInput: string;
  conditionIds: string[];
  marketNames: Record<string, string>;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_POLYMARKET_TRADE_VOLUME_CREATE_FORM_VALUES: PolymarketTradeVolumeCreateFormValues = {
  eventSlugInput: '',
  conditionIds: [],
  marketNames: {},
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const polymarketTradeVolumeCreateValidationSchema = Yup.object().shape({
  eventSlugInput: Yup.string()
    .required('Event URL or slug is required')
    .test('valid-slug', 'Must be a valid Polymarket event URL or slug', (value) => {
      if (!value) return false;
      const trimmed = value.trim();
      const urlMatch = trimmed.match(/polymarket\.com\/(?:[a-z]{2}\/)?event\/([a-z0-9-]+)/i);
      if (urlMatch) return true;
      return /^[a-z0-9][a-z0-9-]*[a-z0-9]$/i.test(trimmed);
    }),
  conditionIds: Yup.array()
    .of(Yup.string().required())
    .min(1, 'Select at least one market')
    .required('Select at least one market'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export enum HypurrCardOperationType {
  Borrow = 'borrow',
  Withdraw = 'withdraw',
}

export enum StripePaymentEventType {
  CheckoutSessionCompleted = 'checkout.session.completed',
  InvoicePaid = 'invoice.paid',
  ChargeSucceeded = 'charge.succeeded',
}

export const STRIPE_PAYMENT_EVENT_TYPE_LABELS: Record<StripePaymentEventType, string> = {
  [StripePaymentEventType.CheckoutSessionCompleted]: 'Stripe Checkout / Payment Links',
  [StripePaymentEventType.InvoicePaid]: 'One-off invoices',
  [StripePaymentEventType.ChargeSucceeded]: 'Direct API / PaymentIntent',
};

export interface HypurrCardTransactionsTriggerFormValues {
  operationType: HypurrCardOperationType;
  chainId: number;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_HYPURR_CARD_TRANSACTIONS_TRIGGER_FORM_VALUES: HypurrCardTransactionsTriggerFormValues = {
  operationType: HypurrCardOperationType.Borrow,
  chainId: 999,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const hypurrCardTransactionsTriggerValidationSchema = Yup.object().shape({
  operationType: Yup.mixed<HypurrCardOperationType>().required('Operation type is required'),
  chainId: Yup.number().required('Network is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const hibachiAffiliatesTriggerValidationSchema = Yup.object().shape({
  referredFeesLevel: Yup.mixed<HibachiAffiliateReferredFeesLevel>().required('Referred fees level is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const ambientTradeTriggerValidationSchema = Yup.object().shape({
  volumeType: Yup.mixed<AmbientVolumeType>().required('Volume type is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const subgraphBalanceTriggerValidationSchema = Yup.object().shape({
  subgraphName: Yup.string().required('Subgraph name is a required field'),
  tokenCurrency: Yup.string().required('Currency is a required field'),
});

export const INITIAL_INSERT_SMART_CONTRACTS_FORM_VALUES: InsertSmartContractsFormValues = {
  contract: {
    chainId: 1,
    contractAddress: '',
  },
  isTestnet: false,
  sameAbi: false,
};

export const selectTriggersValidationSchema = Yup.object().shape({
  name: Yup.string().required(),
  description: Yup.string().required(),
  type: Yup.string().required(),
  signature: Yup.string().required(),
  hasAdvancedFilters: Yup.boolean(),
  filters: Yup.array().when('hasAdvancedFilters', {
    is: true,
    then: (schema) =>
      schema.of(
        Yup.object().shape({
          argument: Yup.string().required('You must select a field'),
          operator: Yup.string().required('You must select an operator'),
          value: Yup.string()
            .required('You must select a value')
            .when('unit', ([unit]) => {
              if (!unit) {
                return Yup.string();
              }
              if (unit === UintArgumentUnitType.Wei) {
                return Yup.string().matches(NON_DECIMAL_NUMBERS_REGEX, 'You must enter a valid wei value');
              }

              return Yup.string().matches(ONLY_DECIMAL_POSITIVE_NUMBERS_REGEX, 'You must enter a valid ether value');
            }),
          type: Yup.string(),
          unit: Yup.string().when('type', {
            is: (type: string) => type.includes('int'),
            then: (schema) => schema.required('You must select a unit'),
          }),
        }),
      ),
  }),
  filterMeetCondition: Yup.string().when('hasAdvancedFilters', ([hasAdvancedFilters], schema: Yup.StringSchema) => {
    if (hasAdvancedFilters) {
      return schema.required('You must select a filter meet condition');
    }
    return schema;
  }),
});

export const contractValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('You must select a network'),
  contractAddress: Yup.string()
    .matches(WALLET_REGEX, 'You must enter a valid contract address')
    .required('You must select a contract'),
});

export const smartContractValidationSchema = Yup.object().shape({
  contract: contractValidationSchema,
  isTestnet: Yup.boolean(),
  sameAbi: Yup.string().when('contracts', {
    is: (contracts: []) => contracts?.length > 1,
    then: (schema) =>
      schema.required(
        'All smart contacts within a same trigger need to share the same ABI. Make sure to check this box or consider splitting this trigger into two campaigns.',
      ),
  }),
});

export type VolumeArgumentType = 'fromTriggerArgument' | 'fromCalculationBasedOnTriggerArguments' | 'noValueAssociated';
export type RevenueArgumentType =
  | 'fromTriggerArgument'
  | 'fromCalculationBasedOnTriggerArguments'
  | 'noRevenueAssociated';
export type CurrencyArgumentType = 'fromToken' | 'fromTriggerArgument';
export type EndUserIdentifierExpressionType = 'from' | 'fromTriggerArgument';

export type ConfigureTriggerFormValues = {
  volumeArgument: {
    type: VolumeArgumentType;
    value?: string;
    percentage?: number;
  };
  currencyVolumeArgument: {
    type?: CurrencyArgumentType;
    value?: string;
  };
  revenueArgument: {
    type: RevenueArgumentType;
    value?: string;
    percentage?: number;
  };
  currencyRevenueArgument: {
    type?: CurrencyArgumentType;
    value?: string;
  };
  endUserIdentifierExpression: {
    type: EndUserIdentifierExpressionType;
    value: string | null;
  };
};

export const INITIAL_CONFIGURE_TRIGGER_FORM_VALUES: ConfigureTriggerFormValues = {
  volumeArgument: {
    type: 'noValueAssociated',
    value: 'msgValue',
    percentage: undefined,
  },
  currencyVolumeArgument: {
    type: undefined,
    value: undefined,
  },
  revenueArgument: {
    type: 'noRevenueAssociated',
    value: 'msgValue',
    percentage: undefined,
  },
  currencyRevenueArgument: {
    type: undefined,
    value: undefined,
  },
  endUserIdentifierExpression: {
    type: 'from',
    value: null,
  },
};

const volumeArgumentValidationSchema = Yup.object().shape({
  type: Yup.string().required('You must select from where the value will be taken'),
  value: Yup.string().when('type', {
    is: (type: VolumeArgumentType) => type !== 'noValueAssociated',
    then: (schema) => schema.required('You must select a value argument'),
  }),
  percentage: Yup.number().when('type', {
    is: (type: VolumeArgumentType) => type === 'fromCalculationBasedOnTriggerArguments',
    then: (schema) =>
      schema
        .required('You must supply a percentage')
        .max(100, 'The maximum value is 100')
        .notOneOf([0], 'The value must be greater than 0'),
  }),
});

const revenueArgumentValidationSchema = Yup.object().shape({
  type: Yup.string().required("You must select from where the transaction's revenue will be taken"),
  value: Yup.string().when('type', {
    is: (type: RevenueArgumentType) => type !== 'noRevenueAssociated',
    then: (schema) => schema.required('You must select a revenue argument'),
  }),
  percentage: Yup.number().when('type', {
    is: (type: RevenueArgumentType) => type === 'fromCalculationBasedOnTriggerArguments',
    then: (schema) =>
      schema
        .required('You must supply a percentage')
        .max(100, 'The maximum value is 100')
        .notOneOf([0], 'The value must be greater than 0'),
  }),
});

const currencyVolumeArgumentSchema = Yup.object().when('volumeArgument', {
  is: (volumeArgument: { type: VolumeArgumentType }) => volumeArgument.type !== 'noValueAssociated',
  then: (schema) =>
    schema.shape({
      type: Yup.string().required('You must select from where the currency will be taken'),
      value: Yup.string().when('type', {
        is: (type: CurrencyArgumentType) => type === 'fromTriggerArgument' || type === 'fromToken',
        then: (schema) => schema.required('You must select a currency argument'),
      }),
    }),
});

const currencyRevenueArgumentSchema = Yup.object().when('revenueArgument', {
  is: (revenueArgument: { type: RevenueArgumentType }) => revenueArgument.type !== 'noRevenueAssociated',
  then: (schema) =>
    schema.shape({
      type: Yup.string().required('You must select from where the currency will be taken'),
      value: Yup.string().when('type', {
        is: (type: CurrencyArgumentType) => type === 'fromTriggerArgument' || type === 'fromToken',
        then: (schema) => schema.required('You must select a currency argument'),
      }),
    }),
});

export const configureTriggerValidationSchema = Yup.object().shape({
  volumeArgument: volumeArgumentValidationSchema,
  revenueArgument: revenueArgumentValidationSchema,
  currencyVolumeArgument: currencyVolumeArgumentSchema,
  currencyRevenueArgument: currencyRevenueArgumentSchema,
});

export interface XActionTriggerFormValues {
  action: XTriggerAction;
  usernameToFollow?: string;
  postText?: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface XFollowTriggerFormValues {
  usernameToFollow: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface XPostTriggerFormValues {
  postText: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface DiscordServerJoinTriggerFormValues {
  guildId: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface DiscordRoleAssignedTriggerFormValues {
  guildId: string;
  roleId: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface StripePaymentTriggerFormValues {
  paymentEventType: StripePaymentEventType;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_STRIPE_PAYMENT_TRIGGER_FORM_VALUES: StripePaymentTriggerFormValues = {
  paymentEventType: StripePaymentEventType.InvoicePaid,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const stripePaymentTriggerFormSchema = Yup.object().shape({
  paymentEventType: Yup.mixed<StripePaymentEventType>()
    .oneOf(Object.values(StripePaymentEventType), 'Invalid payment event type')
    .required('Payment event type is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

/**
 * Venues Exponent balances are read from. These values travel to the backend verbatim and end up
 * inside the trigger signature (`SubgraphBalance-Exponent-${venue}-${vault}`), so they must match
 * `ExponentVenue` in fuul-server and `ExponentBaseClient#getVenue` in subgraph-balance-fetcher
 * exactly. Any drift means the trigger silently never matches an event.
 */
export enum ExponentVenue {
  YtEscrow = 'YtEscrow',
  Orderbook = 'Orderbook',
  Clmm = 'Clmm',
}

/** Display-only labels. Never send these to the backend — it expects the raw {@link ExponentVenue}. */
export const EXPONENT_VENUE_LABELS: Record<ExponentVenue, string> = {
  [ExponentVenue.YtEscrow]: 'YT escrow',
  [ExponentVenue.Orderbook]: 'Orderbook orders',
  [ExponentVenue.Clmm]: 'CLMM liquidity',
};

export interface ExponentTriggerFormValues {
  vaultAddress: string;
  venue: ExponentVenue;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_EXPONENT_TRIGGER_FORM_VALUES: ExponentTriggerFormValues = {
  vaultAddress: '',
  venue: ExponentVenue.YtEscrow,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const exponentTriggerValidationSchema = Yup.object().shape({
  vaultAddress: Yup.string().required('Vault address is required'),
  venue: Yup.mixed<ExponentVenue>().oneOf(Object.values(ExponentVenue), 'Invalid venue').required('Venue is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface XRepostTriggerFormValues {
  postUrl: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface XLikeTriggerFormValues {
  postUrl: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const updateTriggerExpressionsValidationSchema = (options: {
  requireVolume: boolean;
  requireRevenue: boolean;
}) =>
  Yup.object().shape(
    {
      volumeArgument: Yup.object().when('revenueArgument', {
        is: (revenueArgument: { type: RevenueArgumentType }) =>
          revenueArgument?.type === 'noRevenueAssociated' || !revenueArgument?.type,
        then: (schema) =>
          options.requireVolume
            ? volumeArgumentValidationSchema.required('You must define the value argument')
            : schema.notRequired(),
      }),
      revenueArgument: Yup.object().when('volumeArgument', {
        is: (volumeArgument: { type: VolumeArgumentType }) =>
          volumeArgument?.type === 'noValueAssociated' || !volumeArgument?.type,
        then: (schema) =>
          options.requireRevenue
            ? revenueArgumentValidationSchema.required('You must define the revenue argument')
            : schema.notRequired(),
      }),
      currencyVolumeArgument: options.requireVolume
        ? currencyVolumeArgumentSchema.required('You must define the volume currency argument')
        : Yup.mixed().notRequired(),

      currencyRevenueArgument: options.requireRevenue
        ? currencyRevenueArgumentSchema.required('You must define the volume currency argument')
        : Yup.mixed().notRequired(),
    },
    [['volumeArgument', 'revenueArgument']],
  );

export type UpdateTriggerExpressionFormValues = {
  volumeArgument: {
    type?: VolumeArgumentType;
    value?: string;
    percentage?: number;
  };
  revenueArgument: {
    type?: RevenueArgumentType;
    value?: string;
    percentage?: number;
  };
  currencyVolumeArgument: {
    type?: CurrencyArgumentType;
    value?: string;
  };
  currencyRevenueArgument: {
    type?: CurrencyArgumentType;
    value?: string;
  };
};

export const INITIAL_UPDATE_TRIGGER_EXPRESSIONS_FORM_VALUES: UpdateTriggerExpressionFormValues = {
  volumeArgument: {
    type: undefined,
    value: undefined,
    percentage: undefined,
  },
  revenueArgument: {
    type: undefined,
    value: undefined,
    percentage: undefined,
  },
  currencyVolumeArgument: {
    type: undefined,
    value: undefined,
  },
  currencyRevenueArgument: {
    type: undefined,
    value: undefined,
  },
};

export enum LiquidityV3Strategy {
  ActiveLiquidity = 'ActiveLiquidity',
  DistributionFormula = 'DistributionFormula',
}

export enum LiquidityV3TokenDenominator {
  Token0 = 'token0',
  Token1 = 'token1',
}

export interface LiquidityV3TriggerFormValues {
  chainId: number | null;
  protocolId?: string;
  protocolName: string;
  poolAddress?: EthereumAddress;
  token_0?: number;
  token_1?: number;
  liquidity?: number;
  inRangeLiquidity: boolean;
  endUserIdentifierProperty?: UserIdentifierProperty;
  strategy: LiquidityV3Strategy;
  tokenDenominator?: LiquidityV3TokenDenominator;
  _poolDetails?: LiquidityPoolDetailsDTO;
  _totalPercentage: number;
}

export const INITIAL_CLAMM_TRIGGER_FORM_VALUES: LiquidityV3TriggerFormValues = {
  chainId: null,
  protocolId: '',
  protocolName: '',
  poolAddress: undefined,
  token_0: undefined,
  token_1: undefined,
  liquidity: undefined,
  inRangeLiquidity: false,
  strategy: LiquidityV3Strategy.ActiveLiquidity,
  tokenDenominator: undefined,
  endUserIdentifierProperty: undefined,
  _poolDetails: undefined,
  _totalPercentage: 0,
};

export const liquidityV3TriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('You must select a network'),
  poolAddress: Yup.string()
    .required('You must complete with Uniswap Liquidity Pool address')
    .matches(WALLET_REGEX, 'Invalid address format'),
  token_0: Yup.number().when('strategy', {
    is: LiquidityV3Strategy.DistributionFormula,
    then: (schema) => schema.required('Set Token 0 percentage'),
    otherwise: (schema) => schema.optional(),
  }),
  token_1: Yup.number().when('strategy', {
    is: LiquidityV3Strategy.DistributionFormula,
    then: (schema) => schema.required('Set Token 1 percentage'),
    otherwise: (schema) => schema.optional(),
  }),
  liquidity: Yup.number().when('strategy', {
    is: LiquidityV3Strategy.DistributionFormula,
    then: (schema) => schema.required('Set liquidity percentage'),
    otherwise: (schema) => schema.optional(),
  }),
  inRangeLiquidity: Yup.boolean().when('strategy', {
    is: LiquidityV3Strategy.DistributionFormula,
    then: (schema) => schema.required(),
    otherwise: (schema) => schema.optional(),
  }),
  strategy: Yup.mixed<LiquidityV3Strategy>().required('Strategy is required'),
  tokenDenominator: Yup.mixed<LiquidityV3TokenDenominator>().when('strategy', {
    is: LiquidityV3Strategy.ActiveLiquidity,
    then: (schema) => schema.required('Token denominator is required for active liquidity strategy'),
    otherwise: (schema) => schema.optional(),
  }),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
  _totalPercentage: Yup.number().when('strategy', {
    is: LiquidityV3Strategy.DistributionFormula,
    then: (schema) =>
      schema.test('sum-is-100', 'The total of token 0, token 1, and liquidity must be 100', function () {
        const { token_0, token_1, liquidity } = this.parent;
        const total = (Number(token_0) || 0) + (Number(token_1) || 0) + (Number(liquidity) || 0);
        return total === 100;
      }),
    otherwise: (schema) => schema.optional(),
  }),
});

/**
 * A V4 pool is a `bytes32` pool id, not a contract address: every pool lives inside the singleton
 * `PoolManager`. 64 hex characters against an address's 40.
 */
export const POOL_ID_REGEX = /^0x[0-9a-fA-F]{64}$/;

/**
 * V4 is active liquidity only — out-of-range positions count zero — so there is no strategy to
 * pick. The denominator stays: the position value has to be expressed in one of the pool's two
 * tokens, and that choice is the reference price the campaign is measured in.
 *
 * The token addresses are deliberately absent. The backend reads the payout currency off each
 * event, so nothing this form sends decides which token gets paid.
 */
export interface LiquidityV4TriggerFormValues {
  chainId: number | null;
  poolId?: string;
  endUserIdentifierProperty?: UserIdentifierProperty;
  tokenDenominator?: LiquidityV3TokenDenominator;
  _poolDetails?: LiquidityPoolDetailsDTO;
}

export const INITIAL_LIQUIDITY_V4_TRIGGER_FORM_VALUES: LiquidityV4TriggerFormValues = {
  chainId: null,
  poolId: undefined,
  tokenDenominator: undefined,
  endUserIdentifierProperty: undefined,
  _poolDetails: undefined,
};

export const liquidityV4TriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('You must select a network'),
  poolId: Yup.string()
    .required('You must complete with the Uniswap V4 pool ID')
    .matches(POOL_ID_REGEX, 'A V4 pool ID is 0x followed by 64 hex characters, not a pool address'),
  // The "pool not found" error the input sets by hand does not survive submit: Formik replaces
  // field errors with whatever the schema returns. Without this, an id that matches the format but
  // does not resolve creates a trigger for a pool that does not exist.
  _poolDetails: Yup.object().required('Pool not verified — enter a valid pool ID and wait for it to resolve'),
  tokenDenominator: Yup.mixed<LiquidityV3TokenDenominator>().required('Position denominator is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const editTriggerValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is a required field'),
  description: Yup.string().required('Description is a required field'),
  ...utilizationThresholdValidationFields,
});

export interface LynxTradeTriggerFilter {
  argument: LynxTradeFilter;
  type: string;
  operator: string;
  value: string | number;
}

export interface LynxTradeTriggerFormValues {
  name: string;
  chainId: number;
  poolAddress: string;
  filters: LynxTradeTriggerFilter[];
  filterMeetCondition: FilterMeetCondition;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export enum LynxTradeFilter {
  LEVERAGE = 'extra_leverage',
  PAIR_TYPE = 'extra_pairType',
  PAIR_ID = 'pairId',
  PNL = 'extra_pnl',
}

export const filterLabelMap = {
  [LynxTradeFilter.LEVERAGE]: 'Leverage',
  [LynxTradeFilter.PAIR_TYPE]: 'Pair Type',
  [LynxTradeFilter.PAIR_ID]: 'Pair ID',
  [LynxTradeFilter.PNL]: 'PnL',
};

export const filterOperatorTypeMap = {
  [LynxTradeFilter.LEVERAGE]: 'int',
  [LynxTradeFilter.PAIR_TYPE]: 'string',
  [LynxTradeFilter.PAIR_ID]: 'string',
  [LynxTradeFilter.PNL]: 'int',
};

export const INITIAL_LYNX_TRADE_TRIGGER_FORM_VALUES: LynxTradeTriggerFormValues = {
  name: '',
  chainId: 1,
  poolAddress: '',
  filters: [
    {
      argument: LynxTradeFilter.LEVERAGE,
      type: filterOperatorTypeMap[LynxTradeFilter.LEVERAGE],
      operator: '==',
      value: '',
    },
  ],
  filterMeetCondition: FilterMeetCondition.ANY,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const lynxTradeTriggerValidationSchema = Yup.object().shape({
  name: Yup.string().required('Name is a required field'),
  chainId: Yup.number().required('Network is a required field'),
  filterMeetCondition: Yup.string().required('Filter meet condition is a required field'),
});

export interface NadoTradeTriggerFormValues {
  chainId: number;
  selectedMarkets: number[];
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const nadoTradeTriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is a required field'),
  selectedMarkets: Yup.array().optional(),
  endUserIdentifierProperty: Yup.string().required('User identifier is required'),
});

export const INITIAL_NADO_TRADE_TRIGGER_FORM_VALUES: NadoTradeTriggerFormValues = {
  chainId: NETWORKS.find((network) => network.canBeUsedForNadoTrade && !network.testnet)?.chainId ?? 57073,
  selectedMarkets: [],
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const NADO_ADDRESSES_MAPPER = {
  [Network.ARBITRUM]: '0xA4369d8E3dC847aEDf17F4125f1ABB1BC18FC060',
  [Network.MANTLE]: '0x736A2ADa5F4700d49da6b28A74c4A77CDb3E2994',
  [Network.BASE]: '0xd0899a1235771733e89b674633981e422aa45B1A',
  [Network.SONIC]: '0x4B7A7df15Eb80a1CCEc5ab0DBFDF0f6Bf739AB5E',
  [Network.ABSTRACT]: '0xbd5A5fb003F9aBA011E1E01763e6F99c681D2c20',
  [Network.SEI]: '0x56Ffa2fD437C3a718322ea701bEd40560745456e',
};

export interface MorphoTriggerFormValues {
  name: string;
  chainId: number;
  contractAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_MORPHO_TRIGGER_FORM_VALUES: MorphoTriggerFormValues = {
  name: '',
  chainId: 1,
  contractAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const tokenMorphoValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is a required field'),
});

export interface MorphoVaultsTriggerFormValues {
  vault: string;
  chainId: number;
  endUserIdentifierProperty: UserIdentifierProperty;
  isVaultValidated: boolean;
}

export const INITIAL_MORPHO_VAULTS_TRIGGER_FORM_VALUES: MorphoVaultsTriggerFormValues = {
  vault: '',
  chainId: 1,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
  isVaultValidated: false,
};

export const morphoVaultsValidationSchema = Yup.object().shape({
  vault: Yup.string().required('Vault address is required').matches(WALLET_REGEX, 'Invalid address format'),
  chainId: Yup.number().required('Network is a required field'),
});

export const nadoMarkets = [
  { product_id: 0, symbol: 'USDC' },
  { product_id: 1, symbol: 'WBTC' },
  { product_id: 2, symbol: 'BTC-PERP' },
  { product_id: 3, symbol: 'WETH' },
  { product_id: 4, symbol: 'ETH-PERP' },
  { product_id: 5, symbol: 'ARB' },
  { product_id: 6, symbol: 'ARB-PERP' },
  { product_id: 8, symbol: 'BNB-PERP' },
  { product_id: 10, symbol: 'XRP-PERP' },
  { product_id: 12, symbol: 'SOL-PERP' },
  { product_id: 14, symbol: 'POL-PERP' },
  { product_id: 16, symbol: 'SUI-PERP' },
  { product_id: 18, symbol: 'OP-PERP' },
  { product_id: 20, symbol: 'APT-PERP' },
  { product_id: 22, symbol: 'LTC-PERP' },
  { product_id: 24, symbol: 'BCH-PERP' },
  { product_id: 26, symbol: 'COMP-PERP' },
  { product_id: 28, symbol: 'MKR-PERP' },
  { product_id: 30, symbol: 'MPEPE-PERP' },
  { product_id: 31, symbol: 'USDT' },
  { product_id: 34, symbol: 'DOGE-PERP' },
  { product_id: 36, symbol: 'LINK-PERP' },
  { product_id: 38, symbol: 'DYDX-PERP' },
  { product_id: 40, symbol: 'CRV-PERP' },
  { product_id: 41, symbol: 'VRTX' },
  { product_id: 44, symbol: 'TIA-PERP' },
  { product_id: 46, symbol: 'PYTH-PERP' },
  { product_id: 50, symbol: 'JTO-PERP' },
  { product_id: 52, symbol: 'AVAX-PERP' },
  { product_id: 54, symbol: 'INJ-PERP' },
  { product_id: 56, symbol: 'SNX-PERP' },
  { product_id: 58, symbol: 'ADA-PERP' },
  { product_id: 60, symbol: 'IMX-PERP' },
  { product_id: 62, symbol: 'MEME-PERP' },
  { product_id: 64, symbol: 'SEI-PERP' },
  { product_id: 66, symbol: 'BLUR-PERP' },
  { product_id: 68, symbol: 'STX-PERP' },
  { product_id: 70, symbol: 'NEAR-PERP' },
  { product_id: 72, symbol: 'LDO-PERP' },
  { product_id: 74, symbol: 'FIL-PERP' },
  { product_id: 76, symbol: 'WLD-PERP' },
  { product_id: 78, symbol: 'ICP-PERP' },
  { product_id: 80, symbol: 'DOT-PERP' },
  { product_id: 82, symbol: 'TRX-PERP' },
  { product_id: 84, symbol: 'GALA-PERP' },
  { product_id: 86, symbol: 'ATOM-PERP' },
  { product_id: 88, symbol: 'APE-PERP' },
  { product_id: 90, symbol: 'JUP-PERP' },
  { product_id: 96, symbol: 'GMCI30-PERP' },
  { product_id: 98, symbol: 'GMMEME-PERP' },
  { product_id: 100, symbol: 'TON-PERP' },
  { product_id: 102, symbol: 'FTM-PERP' },
  { product_id: 104, symbol: 'WIF-PERP' },
  { product_id: 106, symbol: 'ONDO-PERP' },
  { product_id: 108, symbol: 'ENA-PERP' },
  { product_id: 110, symbol: 'MNT-PERP' },
  { product_id: 114, symbol: 'BLAST-PERP' },
  { product_id: 121, symbol: 'TRUMPWIN' },
  { product_id: 125, symbol: 'HARRISWIN' },
  { product_id: 130, symbol: 'AAVE-PERP' },
  { product_id: 132, symbol: 'ZRO-PERP' },
  { product_id: 134, symbol: 'EIGEN-PERP' },
  { product_id: 138, symbol: 'SCR-PERP' },
  { product_id: 140, symbol: 'DEGEN-PERP' },
  { product_id: 142, symbol: 'GOAT-PERP' },
  { product_id: 144, symbol: 'BRETT-PERP' },
  { product_id: 148, symbol: 'TRUMP-PERP' },
];

export interface PendleYTTriggerFormValues {
  chainId: number;
  ytAddress: string;
  syAddress: string;
  volumeCurrencyAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_PENDLE_YT_TRIGGER_FORM_VALUES: PendleYTTriggerFormValues = {
  chainId: 1,
  ytAddress: '',
  syAddress: '',
  volumeCurrencyAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const pendleYTTriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is a required field'),
  ytAddress: Yup.string().required('YT Address is a required field'),
  syAddress: Yup.string().required('SY Address is a required field'),
  volumeCurrencyAddress: Yup.string().required('Price reference token is required'),
});

export interface PendleLPTriggerFormValues {
  chainId: number;
  lpAddress: string;
  syAddress: string;
  volumeCurrencyAddress: string;
  liquidLockers: LiquidLocker[];
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_PENDLE_LP_TRIGGER_FORM_VALUES: PendleLPTriggerFormValues = {
  chainId: 1,
  lpAddress: '',
  syAddress: '',
  volumeCurrencyAddress: '',
  liquidLockers: [{ address: '', receiptToken: '' }],
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const pendleLPTriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is a required field'),
  lpAddress: Yup.string().required('LP Address is a required field'),
  syAddress: Yup.string().required('SY Address is a required field'),
  volumeCurrencyAddress: Yup.string().required('Price reference token is required'),
  liquidLockers: Yup.array()
    .of(
      Yup.object()
        .shape({
          address: Yup.string().matches(WALLET_REGEX, 'Must be a valid Ethereum address'),
          receiptToken: Yup.string().matches(WALLET_REGEX, 'Must be a valid Ethereum address'),
        })
        .test('both-or-none', 'Both fields must be filled or both must be empty', function (value) {
          const { address, receiptToken } = value;
          const hasAddress = address && address.length > 0;
          const hasReceiptToken = receiptToken && receiptToken.length > 0;

          return (hasAddress && hasReceiptToken) || (!hasAddress && !hasReceiptToken);
        }),
    )
    .min(1, 'At least one liquid locker is required'),
});

export interface ZealyTriggerFormValues {
  name: string;
  description: string;
  questUrl: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_ZEALY_TRIGGER_FORM_VALUES: ZealyTriggerFormValues = {
  name: '',
  description: '',
  questUrl: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const zealyTriggerFormSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string().optional(),
  questUrl: Yup.string()
    .required('Zealy quest URL is required')
    .matches(
      /^https:\/\/zealy\.io\/cw\/([^\/]+)\/questboard\/[^\/]+\/([^\/]+)$/,
      'Must be a valid Zealy quest URL, e.g., https://zealy.io/cw/subdomain/questboard/.../questId',
    ),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface ZealyTriggerData {
  subdomain: string;
  quest_id: string;
}

export interface GalxeTriggerFormValues {
  name: string;
  description: string;
  questUrl: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_GALXE_TRIGGER_FORM_VALUES: GalxeTriggerFormValues = {
  name: '',
  description: '',
  questUrl: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const galxeTriggerFormSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  description: Yup.string().optional(),
  questUrl: Yup.string()
    .required('Galxe quest URL is required')
    .matches(
      /^https:\/\/app\.galxe\.com\/quest\/[^\/]+\/([^\/]+)$/,
      'Must be a valid Galxe quest URL, e.g., https://app.galxe.com/quest/CampaignName/QuestId',
    ),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface GalxeTriggerData {
  quest_id: string;
}

export interface HibachiTradingTriggerFormValues {
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_HIBACHI_TRADING_TRIGGER_FORM_VALUES: HibachiTradingTriggerFormValues = {
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const hibachiTradingTriggerFormSchema = Yup.object().shape({
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface OrderlyBuilderTradesTriggerFormValues {
  brokerId: string;
  volumeType: OrderlyVolumeType;
  revenueType: OrderlyRevenueType;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_ORDERLY_BUILDER_TRADES_TRIGGER_FORM_VALUES: OrderlyBuilderTradesTriggerFormValues = {
  brokerId: '',
  volumeType: OrderlyVolumeType.AllVolume,
  revenueType: OrderlyRevenueType.RealizedPnl,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const orderlyBuilderTradesTriggerFormSchema = Yup.object().shape({
  brokerId: Yup.string().required('Broker ID is required'),
  volumeType: Yup.string()
    .oneOf(Object.values(OrderlyVolumeType), 'Invalid volume type')
    .required('Volume type is required'),
  revenueType: Yup.string()
    .oneOf(Object.values(OrderlyRevenueType), 'Invalid revenue type')
    .required('Revenue type is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export enum MorphoBorrowerStrategyType {
  BorrowNative = 'borrow-native',
  BorrowUSD = 'borrow-usd',
  CollateralNative = 'collateral-native',
  CollateralUSD = 'collateral-usd',
  SupplyNative = 'supply-native',
  SupplyUSD = 'supply-usd',
}

/** User-facing labels for Morpho Market Strategies trigger types (form + compact details). */
export const MORPHO_MARKET_STRATEGY_TYPE_LABELS: Record<MorphoBorrowerStrategyType, string> = {
  [MorphoBorrowerStrategyType.BorrowNative]: 'Borrow Native',
  [MorphoBorrowerStrategyType.BorrowUSD]: 'Borrow USD',
  [MorphoBorrowerStrategyType.CollateralNative]: 'Collateral Native',
  [MorphoBorrowerStrategyType.CollateralUSD]: 'Collateral USD',
  [MorphoBorrowerStrategyType.SupplyNative]: 'Supply Native',
  [MorphoBorrowerStrategyType.SupplyUSD]: 'Supply USD',
};

export interface MorphoBorrowerStrategyTriggerFormValues {
  chainId: number;
  marketId: string;
  strategyType: MorphoBorrowerStrategyType;
  nativeCurrencyAddress?: string;
  nativeCurrencyDecimals?: number;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface HypurrNetPoolPositionsTriggerFormValues {
  chainId: number;
  hyTokenAddress: string;
  variableDebtTokenAddress: string;
  strategy: HypurrNetPoolPositionsStrategy;
  volumeCurrencyAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export interface HypurrEulerNetPositionsTriggerFormValues {
  chainId: number;
  depositTokenAddress: string;
  debtTokenAddress: string;
  strategy: HypurrEulerNetPositionsStrategy;
  volumeCurrencyAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_MORPHO_BORROWER_STRATEGY_TRIGGER_FORM_VALUES: MorphoBorrowerStrategyTriggerFormValues = {
  chainId: 1,
  marketId: '',
  strategyType: MorphoBorrowerStrategyType.BorrowNative,
  nativeCurrencyAddress: '',
  nativeCurrencyDecimals: 18,
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_HYPURR_NET_POOL_POSITIONS_TRIGGER_FORM_VALUES: HypurrNetPoolPositionsTriggerFormValues = {
  chainId: 999,
  hyTokenAddress: '',
  variableDebtTokenAddress: '',
  strategy: HypurrNetPoolPositionsStrategy.Deposit,
  volumeCurrencyAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const INITIAL_HYPURR_EULER_NET_POSITIONS_TRIGGER_FORM_VALUES: HypurrEulerNetPositionsTriggerFormValues = {
  chainId: 999,
  depositTokenAddress: '',
  debtTokenAddress: '',
  strategy: HypurrEulerNetPositionsStrategy.Deposit,
  volumeCurrencyAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const morphoBorrowerStrategyTriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is required'),
  marketId: Yup.string()
    .required('Market ID is required')
    .matches(/^0x[a-fA-F0-9]{64}$/, 'Market ID must be a valid ID'),
  strategyType: Yup.mixed<MorphoBorrowerStrategyType>().required('Strategy type is required'),
  nativeCurrencyAddress: Yup.string().when('strategyType', {
    is: (type: MorphoBorrowerStrategyType) =>
      type === MorphoBorrowerStrategyType.BorrowNative ||
      type === MorphoBorrowerStrategyType.CollateralNative ||
      type === MorphoBorrowerStrategyType.SupplyNative,
    then: (schema) =>
      schema.required('Price reference token is required').matches(WALLET_REGEX, 'Invalid address format'),
    otherwise: (schema) => schema.optional(),
  }),
  nativeCurrencyDecimals: Yup.number().optional(),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const hypurrNetPoolPositionsTriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is required'),
  hyTokenAddress: Yup.string().required('hyToken address is required').matches(WALLET_REGEX, 'Invalid address format'),
  variableDebtTokenAddress: Yup.string()
    .required('VariableDebtToken address is required')
    .matches(WALLET_REGEX, 'Invalid address format'),
  strategy: Yup.mixed<HypurrNetPoolPositionsStrategy>().required('Strategy is required'),
  volumeCurrencyAddress: Yup.string().required('Price Reference is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export const hypurrEulerNetPositionsTriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is required'),
  depositTokenAddress: Yup.string()
    .required('Deposit Token address is required')
    .matches(WALLET_REGEX, 'Invalid address format'),
  debtTokenAddress: Yup.string()
    .required('Debt Token address is required')
    .matches(WALLET_REGEX, 'Invalid address format'),
  strategy: Yup.mixed<HypurrEulerNetPositionsStrategy>().required('Strategy is required'),
  volumeCurrencyAddress: Yup.string().required('Price Reference is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface HyperliquidTradeTriggerFormValues {
  builderAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_HYPERLIQUID_TRADE_TRIGGER_FORM_VALUES: HyperliquidTradeTriggerFormValues = {
  builderAddress: '0x8af3545a3988b7A46f96F9F1AE40c0e64Fa493C2',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const hyperliquidTradeTriggerFormSchema = Yup.object().shape({
  builderAddress: Yup.string().required('Builder is required').matches(WALLET_REGEX, 'Invalid address format'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface ValiantTradeTriggerFormValues {
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_VALIANT_TRADE_TRIGGER_FORM_VALUES: ValiantTradeTriggerFormValues = {
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const valiantTradeTriggerFormSchema = Yup.object().shape({
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface ValiantLiquidityTriggerFormValues {
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_VALIANT_LIQUIDITY_TRIGGER_FORM_VALUES: ValiantLiquidityTriggerFormValues = {
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const valiantLiquidityTriggerFormSchema = Yup.object().shape({
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface FelixVanillaCollateralTriggerFormValues {
  chainId: number;
  marketId: string;
  asset: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_FELIX_VANILLA_COLLATERAL_TRIGGER_FORM_VALUES: FelixVanillaCollateralTriggerFormValues = {
  chainId: 999,
  marketId: '',
  asset: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const felixVanillaCollateralTriggerFormSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is required'),
  marketId: Yup.string()
    .required('Market ID is required')
    .matches(/^0x[a-fA-F0-9]{64}$/, 'Market ID must be a valid 66-character address (0x + 64 hex characters)'),
  asset: Yup.string().required('Asset is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface FelixCdpCollateralTriggerFormValues {
  collateralAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_FELIX_CDP_COLLATERAL_TRIGGER_FORM_VALUES: FelixCdpCollateralTriggerFormValues = {
  collateralAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const felixCdpCollateralTriggerFormSchema = Yup.object().shape({
  collateralAddress: Yup.string()
    .required('Collateral Address is required')
    .matches(WALLET_REGEX, 'Invalid address format'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface GearboxTriggerFormValues {
  mode: GearboxMode;
  chainId: number;
  assetAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_GEARBOX_TRIGGER_FORM_VALUES: GearboxTriggerFormValues = {
  mode: GearboxMode.TotalPosition,
  chainId: 1,
  assetAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const gearboxTriggerFormSchema = Yup.object().shape({
  mode: Yup.mixed<GearboxMode>().oneOf(Object.values(GearboxMode)).required('Mode is required'),
  chainId: Yup.number().required('Chain is required'),
  assetAddress: Yup.string().required('Asset address is required').matches(WALLET_REGEX, 'Invalid address format'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface PacificaBuilderTradesTriggerFormValues {
  builderCode: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_PACIFICA_BUILDER_TRADES_TRIGGER_FORM_VALUES: PacificaBuilderTradesTriggerFormValues = {
  builderCode: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const pacificaBuilderTradesTriggerFormSchema = Yup.object().shape({
  builderCode: Yup.string().required('Builder code is required'),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface OdysseyBuilderTradesTriggerFormValues {
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_ODYSSEY_BUILDER_TRADES_TRIGGER_FORM_VALUES: OdysseyBuilderTradesTriggerFormValues = {
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const odysseyBuilderTradesValidationSchema = Yup.object().shape({
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface EulerV2LoopingTriggerFormValues {
  depositVault: string;
  borrowVault: string;
  maxLeverage: string;
  maxLeverageOperator: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_EULER_V2_LOOPING_TRIGGER_FORM_VALUES: EulerV2LoopingTriggerFormValues = {
  depositVault: '',
  borrowVault: '',
  maxLeverage: '',
  maxLeverageOperator: 'less_than',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const eulerV2LoopingValidationSchema = Yup.object().shape({
  depositVault: Yup.string()
    .required('Deposit vault address is required')
    .matches(WALLET_REGEX, 'Invalid address format'),
  borrowVault: Yup.string()
    .required('Borrow vault address is required')
    .matches(WALLET_REGEX, 'Invalid address format'),
  maxLeverage: Yup.string(),
  maxLeverageOperator: Yup.string().when('maxLeverage', {
    is: (val: string) => !!val,
    then: (schema) => schema.required('Operator is required'),
  }),
  endUserIdentifierProperty: Yup.mixed<UserIdentifierProperty>().required('End user identifier property is required'),
});

export interface EulerVaultsTriggerFormValues {
  vault: string;
  chainId: number;
  underlyingAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_EULER_VAULTS_TRIGGER_FORM_VALUES: EulerVaultsTriggerFormValues = {
  vault: '',
  chainId: 1,
  underlyingAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const eulerVaultsValidationSchema = Yup.object().shape({
  vault: Yup.string().required('Vault address is required').matches(WALLET_REGEX, 'Invalid address format'),
  chainId: Yup.number().required('Network is a required field'),
  underlyingAddress: Yup.string().required('Price reference is required'),
});

// Spectra YT Trigger
export interface SpectraYTTriggerFormValues {
  chainId: number;
  tokenAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_SPECTRA_YT_TRIGGER_FORM_VALUES: SpectraYTTriggerFormValues = {
  chainId: 1,
  tokenAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const spectraYTTriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is a required field'),
  tokenAddress: Yup.string().required('Token address is required').matches(WALLET_REGEX, 'Invalid address format'),
});

// Spectra LP Trigger
export interface SpectraLPTriggerFormValues {
  chainId: number;
  tokenAddress: string;
  endUserIdentifierProperty: UserIdentifierProperty;
}

export const INITIAL_SPECTRA_LP_TRIGGER_FORM_VALUES: SpectraLPTriggerFormValues = {
  chainId: 1,
  tokenAddress: '',
  endUserIdentifierProperty: UserIdentifierProperty.UserAddress,
};

export const spectraLPTriggerValidationSchema = Yup.object().shape({
  chainId: Yup.number().required('Network is a required field'),
  tokenAddress: Yup.string().required('Token address is required').matches(WALLET_REGEX, 'Invalid address format'),
});

// Trigger selector utilities
export const ALL_TRIGGERS_OPTION_ID = '__ALL_TRIGGERS__';

export type TriggerSelectorOption = Trigger | { id: typeof ALL_TRIGGERS_OPTION_ID; name: string };

export const isAllTriggersOption = (
  option: TriggerSelectorOption,
): option is { id: typeof ALL_TRIGGERS_OPTION_ID; name: string } => {
  return option.id === ALL_TRIGGERS_OPTION_ID;
};

export const filterRealTriggers = (options: TriggerSelectorOption[]): Trigger[] => {
  return options.filter((opt): opt is Trigger => !isAllTriggersOption(opt));
};
